import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { WalletService } from '../../../services/payment/wallet.service';
import { BlockChainHashesResponse, EncashTokensRequest, TokenContractAssignment, TokenDetail, UserWallet } from '../../../interfaces/responses/models';

@Component({
  selector: 'app-wallet',
  templateUrl: './wallet.component.html',
  styleUrls: ['./wallet.component.scss'],
})
export class WalletComponent implements OnInit, OnDestroy {
  wallets: UserWallet[] = [];
  adminWallet: UserWallet | null = null;
  loading: boolean = true;
  error: string | null = null;

  searchNames: string[] = ['admin'];
  newName: string = '';
  bidRoundId: string = '';

  // Store token contracts for each user
  tokenContracts: { [userId: number]: TokenContractAssignment[] } = {};
  tokenContractsLoading: { [userId: number]: boolean } = {};
  tokenContractsError: { [userId: number]: string | null } = {};

  // Store token details for each user
  tokenDetails: { [userId: number]: TokenDetail[] } = {};
  tokenDetailsLoading: { [userId: number]: boolean } = {};
  tokenDetailsError: { [userId: number]: string | null } = {};

  // Store available balances for each user
  availableBalances: { [userId: number]: number } = {};
  availableBalanceLoading: { [userId: number]: boolean } = {};
  availableBalanceError: { [userId: number]: string | null } = {};

  // Store blockchain hashes for current bid round
  blockchainHashes: BlockChainHashesResponse | null = null;
  blockchainHashesLoading: boolean = false;
  blockchainHashesError: string | null = null;

  constructor(private walletService: WalletService) { }

  formatNumber(value: number | string): string {
    // Ensure value is a number
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    // Check if it's a valid number
    if (isNaN(numValue)) {
      return '₹0.00';
    }

    return `₹${numValue.toFixed(2)}`;
  }

  ngOnInit() {
    // Load searchNames from localStorage
    let storedSearchNames = localStorage.getItem('searchNames');
    if (storedSearchNames) {
      this.searchNames = JSON.parse(storedSearchNames);
    }

    // Ensure 'admin' is always in searchNames
    if (!this.searchNames.includes('admin')) {
      this.searchNames.push('admin');
    }

    // Load bidRoundId from localStorage
    const storedBidRoundId = localStorage.getItem('bidRoundId');
    if (storedBidRoundId) {
      this.bidRoundId = storedBidRoundId;
    }

    this.fetchWalletData();
  }

  ngOnDestroy() {
    // Save searchNames to localStorage
    localStorage.setItem('searchNames', JSON.stringify(this.searchNames));

    // Save bidRoundId to localStorage
    localStorage.setItem('bidRoundId', this.bidRoundId);
  }

  getBlockchainHashes() {
    // Skip if already loading or no bid round selected
    if (this.blockchainHashesLoading || !this.bidRoundId || this.bidRoundId.trim() === '') {
      return;
    }
    
    this.blockchainHashes = null;
    this.blockchainHashesError = null;
    this.blockchainHashesLoading = true;
    
    const bidRoundIdParam = this.bidRoundId.trim();
    
    this.walletService.getBlockChainHashes(bidRoundIdParam)
      .subscribe({
        next: (data: BlockChainHashesResponse) => {
          this.blockchainHashes = data;
          this.blockchainHashesLoading = false;
        },
        error: (err: any) => {
          console.error('Error fetching blockchain hashes:', err);
          this.blockchainHashesError = 'Could not load blockchain hashes.';
          this.blockchainHashesLoading = false;
        }
      });
  }

  showHashDetails(hash: any) {
    alert(`Hash Details:\nType: ${hash.hash_type}\nValue: ${hash.hash_value}\nDetails: ${hash.details}`);
  }

  getTokenDetails(userId: number) {
    // Skip if already loading or loaded
    if (this.tokenDetailsLoading[userId]) {
      return;
    }

    // Initialize if not already done
    if (!this.tokenDetails[userId]) {
      this.tokenDetails[userId] = [];
      this.tokenDetailsError[userId] = null;
    }

    this.tokenDetailsLoading[userId] = true;

    // Only pass bidRoundId if it's not empty
    const bidRoundIdParam = this.bidRoundId && this.bidRoundId.trim() !== '' ? this.bidRoundId.trim() : undefined;

    this.walletService.getTokenDetails(userId.toString(), bidRoundIdParam)
      .subscribe({
        next: (data) => {
          console.log(data)
          this.tokenDetails[userId] = data;
          this.tokenDetailsLoading[userId] = false;
        },
        error: (err) => {
          console.error(`Error fetching token details for user ${userId}:`, err);
          this.tokenDetailsError[userId] = 'Could not load token details.';
          this.tokenDetailsLoading[userId] = false;
        }
      });
  }

  addName() {
    if (this.newName && this.newName.trim() !== '' && !this.searchNames.includes(this.newName.trim())) {
      this.searchNames.push(this.newName.trim());
      localStorage.setItem('searchNames', JSON.stringify(this.searchNames));
      this.newName = '';
    }
  }

  removeName(index: number) {
    this.searchNames.splice(index, 1);
    localStorage.setItem('searchNames', JSON.stringify(this.searchNames));
  }

  fetchWalletData() {
    this.loading = true;

    // Only pass bidRoundId if it's not empty
    const bidRoundIdParam = this.bidRoundId && this.bidRoundId.trim() !== '' ? this.bidRoundId.trim() : undefined;

    this.walletService.getWallets(this.searchNames, bidRoundIdParam)
      .subscribe({
        next: (data: UserWallet[]) => {
          // Separate admin wallet from others
          this.adminWallet = data.find(w => w.user_name.toLowerCase() === 'admin') || null;
          this.wallets = data
            .filter(w => w.user_name.toLowerCase() !== 'admin')
            .sort((a, b) => a.user_name.localeCompare(b.user_name));
          this.loading = false;

          // Load data for all wallets including admin
          if (data && data.length > 0) {
            data.forEach(wallet => {
              // Only load token details for non-admin wallets
              if (wallet.user_name.toLowerCase() !== 'admin') {
                this.getTokenContracts(wallet.user_id);
                this.getTokenDetails(wallet.user_id);
                this.getAvailableBalance(wallet.user_id);
              }
            });
            // Load blockchain hashes once for the bid round
            this.getBlockchainHashes();
          }
        },
        error: (err: any) => {
          console.error('Error fetching wallet data:', err);
          this.error = 'Could not load data. Please try again.';
          this.loading = false;
        }
      });
  }

  viewDetails(userId: number) {
    console.log(`Ver detalles para el usuario con ID: ${userId}`);
  }

  encashTokens(userId: number) {
    const amount = prompt('Enter amount to encash:');
    if (amount === null) {
      return; // User cancelled the prompt
    }
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      alert('Please enter a valid positive number');
      return;
    }
    
    const data: EncashTokensRequest = {
      amount: numAmount,
      user: userId.toString()
    };
    
    this.walletService.encashTokens(data).subscribe({
      next: (response) => {
        console.log('Tokens encashed successfully:', response);
        alert(`Tokens encashed successfully. Transaction ID: ${response.hygge_transaction_id}`);
        // Refresh wallet data to show updated balances
        this.fetchWalletData();
      },
      error: (err) => {
        console.error('Error encashing tokens:', err);
        alert(`Error encashing tokens: ${err.error?.message || 'Unknown error'}`);
      }
    });
  }
  
  getTokenContracts(userId: number) {
    // Skip if already loading or loaded
    if (this.tokenContractsLoading[userId]) {
      return;
    }
    
    // Initialize if not already done
    if (!this.tokenContracts[userId]) {
      this.tokenContracts[userId] = [];
      this.tokenContractsError[userId] = null;
    }
    
    this.tokenContractsLoading[userId] = true;
    
    // Only pass bidRoundId if it's not empty
    const bidRoundIdParam = this.bidRoundId && this.bidRoundId.trim() !== '' ? this.bidRoundId.trim() : undefined;
    
    this.walletService.getTokenContracts(userId.toString(), bidRoundIdParam)
    .subscribe({
      next: (data) => {
        this.tokenContracts[userId] = data;
        this.tokenContractsLoading[userId] = false;
      },
      error: (err) => {
        console.error(`Error fetching token contracts for user ${userId} in bid round ${bidRoundIdParam || 'none'}:`, err);
        this.tokenContractsError[userId] = 'Could not load token contracts.';
        this.tokenContractsLoading[userId] = false;
      }
    });
  
  }
  
  // Helper method to group token contracts by type
  getContractsByType(userId: number, type: string): TokenContractAssignment[] {
    if (!this.tokenContracts[userId]) {
      return [];
    }
    return this.tokenContracts[userId].filter(contract => contract.type === type);
  }

  // Helper method to organize token details by charge type
  getTokenDetailsByChargeType(userId: number, chargeType: string): TokenDetail[] {
    if (!this.tokenDetails[userId]) {
      return [];
    }
    return this.tokenDetails[userId].filter(detail => detail.charge_type === chargeType);
  }

  // Method to fetch available balance for a user
  getAvailableBalance(userId: number) {
    // Skip if already loading
    if (this.availableBalanceLoading[userId]) {
      return;
    }
    
    // Initialize if not already done
    if (this.availableBalances[userId] === undefined) {
      this.availableBalances[userId] = 0;
      this.availableBalanceError[userId] = null;
    }
    
    this.availableBalanceLoading[userId] = true;
    
    this.walletService.getAvailableBalance(userId.toString())
      .subscribe({
        next: (data) => {
          console.log(`Available balance for user ${userId}:`, data);
          // Convert balance to number (it might come as a string from API)
          this.availableBalances[userId] = typeof data.balance === 'string' 
            ? parseFloat(data.balance) 
            : data.balance;
          this.availableBalanceLoading[userId] = false;
        },
        error: (err) => {
          console.error(`Error fetching available balance for user ${userId}:`, err);
          this.availableBalanceError[userId] = 'Could not load available balance.';
          this.availableBalanceLoading[userId] = false;
        }
      });
  }
}
